import React from 'react';
import type { EmailDomainResponse } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import { DomainStatusBadge } from './DomainStatusBadge';

/**
 * Linha de tabela de domínio de e-mail (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + coluna multi-linha (domínio + região) + menu ••• (última col).
 * Ícone sem foto = slate #64748b via AvatarFallback.
 */
export interface EmailDomainListItemProps {
  domain: EmailDomainResponse;
  selected?: boolean;
  verifying?: boolean;
  onToggleSelect?: (id: string) => void;
  onView?: (id: string) => void;
  onVerify?: (id: string) => void;
  onDelete?: (domain: EmailDomainResponse) => void;
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return '—';
  }
};

const getVerifiedRecordsCount = (domain: EmailDomainResponse): string => {
  if (!domain.dnsRecords || domain.dnsRecords.length === 0) return '0/0';
  const verified = domain.dnsRecords.filter((r) => r.status === 'verified').length;
  return `${verified}/${domain.dnsRecords.length}`;
};

export const EmailDomainListItem: React.FC<EmailDomainListItemProps> = ({
  domain,
  selected = false,
  verifying = false,
  onToggleSelect,
  onView,
  onVerify,
  onDelete,
}) => {
  const actions: CardAction[] = [];
  if (onView) actions.push({ label: 'Detalhes', icon: 'eye', onClick: () => onView(domain.id) });
  if (onVerify && domain.status !== 'verified') {
    actions.push({ label: 'Verificar', icon: 'refresh', onClick: () => onVerify(domain.id), disabled: verifying });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(domain), variant: 'danger' });
  }

  return (
    <tr
      className="border-b transition-colors cursor-pointer"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      onClick={() => onView?.(domain.id)}
      data-testid={`domain-row-${domain.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onToggleSelect?.(domain.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`domain-check-${domain.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Domínio: ícone + nome + região */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-9 h-9 flex-shrink-0" isSquircle>
            <AvatarFallback isSquircle>
              <Icon name="mail" className="w-4 h-4 text-white" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }} title={domain.domain}>
              {domain.domain}
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
              {domain.region || '—'}
            </div>
          </div>
        </div>
      </td>

      {/* Verificação */}
      <td className="px-4 py-3">
        <DomainStatusBadge status={domain.status} />
      </td>

      {/* Registros DNS */}
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
          <Icon name="check-circle" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
          {getVerifiedRecordsCount(domain)}
        </span>
      </td>

      {/* Capacidades */}
      <td className="px-4 py-3">
        {(domain.sendingEnabled || domain.receivingEnabled) ? (
          <div className="flex flex-wrap gap-1.5">
            {domain.sendingEnabled && (
              <span className="inline-flex items-center gap-1 h-[22px] px-2 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
                <Icon name="send" className="w-3 h-3" /> Envio
              </span>
            )}
            {domain.receivingEnabled && (
              <span className="inline-flex items-center gap-1 h-[22px] px-2 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
                <Icon name="download" className="w-3 h-3" /> Recebimento
              </span>
            )}
          </div>
        ) : (
          <span style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Adicionado */}
      <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>
        {formatDate(domain.createdAt)}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        <CardActionsMenu actions={actions} size="sm" data-testid={`domain-actions-${domain.id}`} />
      </td>
    </tr>
  );
};

EmailDomainListItem.displayName = 'EmailDomainListItem';
